import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Arrow, Check } from "./icons";
import {
  BillingSaving,
  billingDescription,
  billingPrice,
  BillingToggle,
  type BillingTerm,
} from "./billing-toggle";
import { Reveal } from "./reveal";
import { productCategories, sharedHostingPlans } from "~/config/products";
import { getPurchaseUrl } from "~/config/purchase";

export function PlanCards({
  billingTerm = "monthly",
  cardSurface = "white",
  linkToProduct = false,
  includeCustom = false,
}: {
  billingTerm?: BillingTerm;
  cardSurface?: "white" | "cream";
  linkToProduct?: boolean;
  includeCustom?: boolean;
}) {
  return (
    <div className="grid gap-5 lg:grid-cols-3 lg:gap-6">
      {sharedHostingPlans.map((plan) => (
        <section
          key={plan.name}
          className={`flex h-full min-h-[28rem] flex-col rounded-[1.75rem] border p-7 ${plan.featured ? "border-ink bg-ink text-cream shadow-[0_18px_35px_rgba(13,43,34,.2)]" : `border-ink/10 ${cardSurface === "cream" ? "bg-cream" : "bg-white"} shadow-[0_2px_0_rgba(13,43,34,.03)]`}`}
        >
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-3xl font-bold tracking-tight">{plan.name}</h3>
            </div>
            {plan.featured && (
              <span className="rounded-full bg-leaf px-3 py-1 text-[10px] font-black tracking-[.1em] text-ink">
                GOOD FIT
              </span>
            )}
          </div>
          <p
            className={`mt-3 min-h-12 leading-6 ${plan.featured ? "text-cream/65" : "text-ink/60"}`}
          >
            {plan.note}
          </p>
          <div className="mt-7">
            <p className="text-5xl font-bold tracking-tight">
              {billingTerm === "monthly"
                ? plan.price
                : billingPrice(plan.price, billingTerm)}
              <span
                className={`text-sm font-medium ${plan.featured ? "text-cream/55" : "text-ink/50"}`}
              >
                {billingTerm === "monthly" ? " / month" : " total"}
              </span>
            </p>
            {billingTerm !== "monthly" && (
              <p
                className={`mt-1 text-xs ${plan.featured ? "text-cream/65" : "text-ink/60"}`}
              >
                {billingDescription(plan.price, billingTerm)}
              </p>
            )}
            <BillingSaving term={billingTerm} />
          </div>
          <ul className="mt-8 grid gap-3.5 border-t border-current/10 pt-6">
            {plan.features.map((feature) => (
              <li key={feature} className="flex items-center gap-3 text-sm">
                <Check />
                {feature}
              </li>
            ))}
          </ul>
          <a
            href={
              linkToProduct
                ? "/products/web-hosting/shared-hosting#plans"
                : getPurchaseUrl({
                    product: "webHosting",
                    plan: plan.name,
                    billingTerm,
                  })
            }
            {...(!linkToProduct ? { target: "_blank", rel: "noreferrer" } : {})}
            className={`mt-9 block rounded-full px-5 py-3 text-center text-sm font-bold transition motion-safe:hover:-translate-y-0.5 focus-visible:outline-3 focus-visible:outline-leaf focus-visible:outline-offset-3 ${plan.featured ? "button-accent" : "border border-ink/15 bg-white hover:border-moss/35 hover:bg-cloud"}`}
          >
            {linkToProduct ? "See plan details" : `Choose ${plan.name}`}
          </a>
        </section>
      ))}
      {includeCustom && (
        <section className="flex min-h-56 flex-col rounded-[1.75rem] border border-dashed border-moss/35 bg-cloud/45 p-7 lg:col-span-3 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
          <div>
            <p className="eyebrow text-moss">Need more room?</p>
            <h3 className="mt-4 text-3xl font-bold tracking-tight">
              Custom hosting
            </h3>
            <p className="mt-3 max-w-2xl leading-6 text-ink/65">
              For projects that need more storage, domains, databases, or
              specialised assistance.
            </p>
          </div>
          <a
            href="/contact"
            className="mt-6 rounded-full border border-moss bg-white px-5 py-3 text-center text-sm font-bold text-ink transition hover:-translate-y-0.5 hover:bg-leaf focus-visible:outline-3 focus-visible:outline-leaf focus-visible:outline-offset-3 lg:mt-0 lg:shrink-0"
          >
            Contact us
          </a>
        </section>
      )}
    </div>
  );
}

export function HomeProductHighlights() {
  const [billingTerm, setBillingTerm] = useState<BillingTerm>("monthly");

  return (
    <section className="border-y border-ink/10 bg-white">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-12">
        <Reveal>
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div>
              <h2 className="display max-w-2xl text-4xl font-bold leading-[.95] sm:text-5xl">
                Room for your next good idea.
              </h2>
              <p className="mt-4 max-w-xl text-[1.0625rem] leading-7 text-ink/60">
                Start with the essentials and choose the space your website
                needs today.
              </p>
            </div>
            <div className="flex flex-col items-start gap-3 lg:items-end">
              <p className="text-sm font-semibold text-moss lg:text-right">
                Save more with longer terms
              </p>
              <BillingToggle value={billingTerm} onChange={setBillingTerm} />
              <Link
                to="/products/web-hosting/shared-hosting"
                className="group flex items-center gap-2 text-sm font-bold text-moss"
              >
                Compare hosting plans <Arrow />
              </Link>
            </div>
          </div>
        </Reveal>
        <div className="mt-9">
          <PlanCards
            billingTerm={billingTerm}
            cardSurface="cream"
            linkToProduct
          />
        </div>
      </div>
    </section>
  );
}

export function ProductCatalogue() {
  return (
    <div className="mx-auto grid max-w-7xl gap-5 px-5 py-16 sm:grid-cols-2 xl:grid-cols-4 lg:px-12">
      {productCategories
        .filter((category) => category.slug === "web-hosting")
        .map((category) => (
          <Link
            key={category.id}
            to="/products/$category"
            params={{ category: category.slug }}
            className="group flex min-h-60 flex-col rounded-3xl border border-ink/10 bg-white p-6 transition duration-300 hover:-translate-y-1 hover:border-moss hover:shadow-[5px_5px_0_#dce9e2]"
          >
            <p className="eyebrow text-moss">Product category</p>
            <h2 className="mt-5 text-2xl font-bold tracking-tight">
              {category.name}
            </h2>
            <p className="mt-3 text-sm leading-6 text-ink/60">
              {category.description}
            </p>
            <p className="mt-auto pt-8 text-sm text-ink/50">
              {category.products.length}{" "}
              {category.products.length === 1 ? "service" : "services"}
            </p>
            <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-moss">
              Browse category <Arrow />
            </span>
          </Link>
        ))}
    </div>
  );
}

export function CategoryProducts({
  category,
}: {
  category: (typeof productCategories)[number];
}) {
  return (
    <div className="mx-auto max-w-7xl px-5 py-14 lg:px-12">
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {category.products.map((product) => (
          <a
            key={product.slug}
            href={product.href}
            className="group flex min-h-64 flex-col rounded-3xl border border-ink/10 bg-white p-6 transition duration-300 hover:-translate-y-1 hover:border-moss hover:shadow-[5px_5px_0_#dce9e2]"
          >
            <div className="flex justify-end">
              <Arrow />
            </div>
            <h2 className="mt-8 text-2xl font-bold tracking-tight">
              {product.name}
            </h2>
            <p className="mt-3 text-sm leading-6 text-ink/60">
              {product.description}
            </p>
            <div className="mt-auto flex items-center justify-between gap-3 pt-7">
              <span className="text-sm font-bold">{product.startingAt}</span>
              <span className="inline-flex items-center gap-1 text-sm font-bold text-moss">
                View service <Arrow />
              </span>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
